/**
 * Who is sitting at the table.
 *
 * Two separate ideas, deliberately not merged:
 *
 *   SKILL is how close a seat plays to the best line its own model can find. It is one
 *   number, it only goes down, and every point of it costs the bot money against a
 *   competent player. `chartAdherence` is the honest, measurable half: in push/fold
 *   spots we have an exact solve, so "plays the chart 82% of the time" is a fact about
 *   this bot that can be verified by counting.
 *
 *   STYLE is what kind of player it is at any skill. A loose-aggressive expert and a
 *   tight-passive expert are both experts. Style is what makes a nine-handed table
 *   feel like a table rather than nine copies of one program, and it is also what
 *   makes the leak report useful: you have to learn to beat a calling station and a
 *   maniac differently.
 *
 * Nothing in here is described to a customer as GTO. The bots consult the solved
 * charts where those charts apply and play a heuristic everywhere else, and the copy
 * has to say so.
 */

export type Tier = "novice" | "recreational" | "regular" | "grinder" | "expert";

export interface BotStyle {
  /** Multiplier on preflop range width. 0.6 is a nit, 1.8 plays anything. */
  looseness: number;
  /** Preference for the aggressive option when two lines are close. 0..1. */
  aggression: number;
  /** Bluff frequency against the balanced rate. 0 never bluffs, 2 bluffs twice as often. */
  bluffiness: number;
  /** Willingness to keep calling with marginal holdings. 0..1, 0.5 is neutral. */
  stickiness: number;
  /** Preferred bet as a fraction of the pot, before texture adjusts it. */
  sizingBias: number;
  /** How much position changes its ranges. 0 plays every seat the same. */
  positionAwareness: number;
  /** How hard it narrows an opponent's range from their actions. 0 does not read. */
  handReading: number;
  /** Chance per decision of an out-of-character move. Tilt, spew, curiosity. */
  volatility: number;
  /** Willingness to call a big bet with a bluff-catcher rather than fold. 0..1. */
  callDown: number;
}

export interface BotProfile {
  id: string;
  name: string;
  /**
   * A name that fits in a seat box on a phone.
   *
   * "Calling Station" is the right label in a lineup and wraps to three lines on a
   * nine-handed table at 390 pixels, where it overlaps the two seats beside it.
   */
  short: string;
  tier: Tier;
  /** One line the UI can show. Describes behaviour, never claims a solve. */
  blurb: string;
  /**
   * How often the bot takes the action its own model rates best, 0..1. The rest of the
   * time it takes the second-best, weighted by how close that was — a bad player is
   * not a random player, they are a player whose mistakes are plausible.
   */
  discipline: number;
  /**
   * How often it follows the solved push/fold chart when one applies, 0..1. Measurable
   * by counting, and reported to the player as a fact rather than a claim.
   */
  chartAdherence: number;
  style: BotStyle;
}

const STYLE_BASE: BotStyle = {
  looseness: 1, aggression: 0.5, bluffiness: 1, stickiness: 0.5, sizingBias: 0.6,
  positionAwareness: 1, handReading: 1, volatility: 0.02, callDown: 0.5,
};

function profile(
  id: string, name: string, short: string, tier: Tier, blurb: string,
  discipline: number, chartAdherence: number, style: Partial<BotStyle>
): BotProfile {
  return { id, name, short, tier, blurb, discipline, chartAdherence, style: { ...STYLE_BASE, ...style } };
}

/**
 * The roster. Difficulty rises down the list, and within a tier the styles differ
 * enough that beating one does not mean beating the other.
 */
export const PROFILES: readonly BotProfile[] = [
  profile("whale", "Whale", "Whale", "novice",
    "Plays almost every hand and hates folding. Will pay you off, and will also draw out on you.",
    0.35, 0.30, {
      looseness: 2.2, aggression: 0.25, bluffiness: 0.3, stickiness: 0.9, sizingBias: 0.4,
      positionAwareness: 0.1, handReading: 0.05, volatility: 0.10, callDown: 0.85,
    }),

  profile("station", "Calling Station", "Station", "novice",
    "Calls with anything that might be good and almost never raises. Bluffing this seat is setting money on fire.",
    0.40, 0.35, {
      looseness: 1.6, aggression: 0.12, bluffiness: 0.1, stickiness: 0.95, sizingBias: 0.45,
      positionAwareness: 0.2, handReading: 0.1, volatility: 0.04, callDown: 0.95,
    }),

  profile("maniac", "Maniac", "Maniac", "recreational",
    "Raises constantly with anything. Very profitable to play against, once you stop folding.",
    0.42, 0.35, {
      looseness: 2.0, aggression: 0.92, bluffiness: 2.4, stickiness: 0.5, sizingBias: 0.95,
      positionAwareness: 0.2, handReading: 0.2, volatility: 0.16, callDown: 0.45,
    }),

  profile("rec", "Recreational", "Rec", "recreational",
    "Loose and passive, chases draws, and overvalues top pair. The player everyone hopes to sit next to.",
    0.52, 0.50, {
      looseness: 1.5, aggression: 0.35, bluffiness: 0.5, stickiness: 0.75, sizingBias: 0.5,
      positionAwareness: 0.4, handReading: 0.3, volatility: 0.07, callDown: 0.7,
    }),

  profile("nit", "The Rock", "Rock", "regular",
    "Very tight and very straightforward. When this seat puts money in, believe it.",
    0.68, 0.72, {
      looseness: 0.55, aggression: 0.45, bluffiness: 0.25, stickiness: 0.25, sizingBias: 0.6,
      positionAwareness: 0.8, handReading: 0.7, volatility: 0.01, callDown: 0.25,
    }),

  profile("tag", "Regular", "Reg", "regular",
    "Solid, tight-aggressive, standard lines. Makes few obvious mistakes and few creative plays.",
    0.74, 0.80, {
      looseness: 0.9, aggression: 0.6, bluffiness: 0.9, stickiness: 0.45, sizingBias: 0.6,
      positionAwareness: 1, handReading: 0.8, volatility: 0.02, callDown: 0.45,
    }),

  profile("lag", "Aggressor", "LAG", "grinder",
    "Wide, aggressive, and difficult. Applies pressure in the spots where folding is cheapest for you.",
    0.82, 0.88, {
      looseness: 1.35, aggression: 0.8, bluffiness: 1.5, stickiness: 0.55, sizingBias: 0.75,
      positionAwareness: 1.2, handReading: 1, volatility: 0.03, callDown: 0.55,
    }),

  profile("grinder", "Grinder", "Grind", "grinder",
    "Disciplined, positionally aware, sizes well. Punishes loose calls and thin value bets.",
    0.88, 0.94, {
      looseness: 0.95, aggression: 0.65, bluffiness: 1.05, stickiness: 0.45, sizingBias: 0.65,
      positionAwareness: 1.2, handReading: 1.1, volatility: 0.01, callDown: 0.5,
    }),

  profile("expert", "Expert", "Expert", "expert",
    "Follows the solved push/fold charts exactly and plays a balanced, mixed strategy everywhere else.",
    0.97, 1.0, {
      looseness: 1, aggression: 0.62, bluffiness: 1, stickiness: 0.5, sizingBias: 0.65,
      positionAwareness: 1.3, handReading: 1.25, volatility: 0, callDown: 0.5,
    }),
];

export const PROFILE_BY_ID: ReadonlyMap<string, BotProfile> =
  new Map(PROFILES.map((p) => [p.id, p]));

export function profileById(id: string): BotProfile {
  const p = PROFILE_BY_ID.get(id);
  if (!p) throw new Error(`unknown bot profile: ${id}`);
  return p;
}

export const TIER_ORDER: readonly Tier[] = ["novice", "recreational", "regular", "grinder", "expert"];

/** Where a tier sits, 0 weakest. Used to assert that stronger bots actually win. */
export function tierIndex(t: Tier): number { return TIER_ORDER.indexOf(t); }

/**
 * A table of bots at roughly a requested difficulty, with styles spread out.
 *
 * `difficulty` is 0..1 across the whole roster. A table is not nine copies of the
 * chosen profile: it is a spread centred on it, because every real table has a range
 * of players and learning to notice that IS the skill.
 */
export function tableOf(count: number, difficulty: number, spread = 0.25): BotProfile[] {
  if (count < 0) throw new Error("a table cannot have fewer than zero bots");
  if (!(difficulty >= 0 && difficulty <= 1)) throw new Error("difficulty is 0..1");
  const centre = difficulty * (PROFILES.length - 1);
  const out: BotProfile[] = [];
  for (let i = 0; i < count; i++) {
    // Deterministic fan-out around the centre, alternating below and above, so the
    // same request always produces the same table.
    const step = Math.ceil(i / 2) * (i % 2 === 0 ? -1 : 1);
    const idx = Math.round(centre + step * spread * (PROFILES.length - 1) / 2);
    out.push(PROFILES[Math.max(0, Math.min(PROFILES.length - 1, idx))]!);
  }
  return out;
}
